/**
 * CommandPalette — 全域命令面板（⌘K / Ctrl+K 開啟）
 * ──────────────────────────────────────────────
 * 介面構成：
 *   ┌───────────────────────────────────────────────┐
 *   │ 🔍  輸入框 ………………………………………  ⌘K       │
 *   ├───────────────────────────────────────────────┤
 *   │ ▸ 命令                                         │
 *   │   • 新增公告                                    │
 *   │   • 新建客戶                                    │
 *   │ ▸ 頁面                                         │
 *   │   • 公佈欄                                      │
 *   │   ...                                          │
 *   └───────────────────────────────────────────────┘
 *
 * 互動：
 *   - 自動 focus 輸入框
 *   - ↑ / ↓ 在所有可見結果間導航（跨分組）
 *   - Enter → 觸發選中項（router.push 或自訂 action）
 *   - Esc → 關閉（antd Modal 內建）
 *   - 點擊 row → 觸發
 *
 * Fuzzy 演算法（純前端，無第三方依賴）：
 *   - 將查詢字串小寫；對每個 item 檢視 `title / subtitle / code / keywords`
 *   - 計分規則（高 → 低）：
 *     * title 開頭命中 +100，title 包含 +60
 *     * code 開頭命中 +90，code 包含 +50
 *     * keyword 完整命中 +40，keyword 包含 +20
 *     * subtitle 包含 +10
 *   - 任一欄位都未命中 → 整個 item 排除
 *   - 同分時保持原順序（穩定排序）
 *
 * 視覺：
 *   - Modal width 640，圓角 12px
 *   - 半透明黑遮罩 rgba(15, 23, 42, 0.4) + backdrop blur（透過全域 CSS 注入）
 *   - 結果列：左 icon chip（按 type tone 上色）+ 標題 + mono 編號 + 右 type tag
 *   - 選中行：brand.primarySoft 背景 + 左側 3px brand bar
 */
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input } from 'antd';
import type { InputRef } from 'antd';
import {
  Search,
  FileText,
  Users,
  ClipboardList,
  Award,
  Layers,
  Command as CommandIcon,
  CornerDownLeft,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { useRouter } from '@/lib/navigation';
import StatusPill from '@/components/shell/StatusPill';
import { color, font, radius, space, text, tracking, weight, leading } from '@/styles/tokens';
import { useCommandPaletteStore } from '@/components/command-palette/use-command-palette';
import {
  getAllItems,
  type CommandItem,
  type CommandItemType,
} from '@/components/command-palette/command-data';

/** 每個分組最多展示幾個（避免列表過長） */
const PER_GROUP_LIMIT = 5;

/** LocalStorage key — 紀錄最近選擇的 itemId（最多 5 個） */
const RECENT_KEY = 'zhcb.commandPalette.recent';
const RECENT_LIMIT = 5;

/* ════════════════════════════════════════════════════════
 * 型別 / 工具
 * ════════════════════════════════════════════════════════ */

/** 分組顯示元資料：標題 + 對應 StatusPill tone + 對應圖標 */
const GROUP_META: Record<
  CommandItemType,
  {
    label: string;
    tone: 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'neutral' | 'muted';
    Icon: typeof FileText;
  }
> = {
  command: { label: '命令', tone: 'brand', Icon: CommandIcon },
  page: { label: '頁面', tone: 'info', Icon: FileText },
  client: { label: '客戶', tone: 'success', Icon: Users },
  application: { label: '案件', tone: 'warning', Icon: ClipboardList },
  certificate: { label: '證書', tone: 'neutral', Icon: Award },
  task: { label: '任務', tone: 'muted', Icon: Layers },
};

/** 結果分組順序（命令置頂） */
const GROUP_ORDER: CommandItemType[] = [
  'command',
  'page',
  'client',
  'application',
  'certificate',
  'task',
];

/* ────────────────────────────────────────────────
 * Fuzzy score
 * ──────────────────────────────────────────────── */

function scoreItem(item: CommandItem, query: string): number {
  if (!query) return 0;
  const q = query.toLowerCase();
  let score = 0;
  let hit = false;

  const title = item.title.toLowerCase();
  if (title.startsWith(q)) {
    score += 100;
    hit = true;
  } else if (title.includes(q)) {
    score += 60;
    hit = true;
  }

  if (item.code) {
    const c = item.code.toLowerCase();
    if (c.startsWith(q)) {
      score += 90;
      hit = true;
    } else if (c.includes(q)) {
      score += 50;
      hit = true;
    }
  }

  if (item.subtitle) {
    if (item.subtitle.toLowerCase().includes(q)) {
      score += 10;
      hit = true;
    }
  }

  if (item.keywords) {
    for (const kw of item.keywords) {
      const k = kw.toLowerCase();
      if (k === q) {
        score += 40;
        hit = true;
      } else if (k.includes(q)) {
        score += 20;
        hit = true;
      }
    }
  }

  return hit ? score : -1;
}

/* ────────────────────────────────────────────────
 * Recent 紀錄（localStorage）
 * ──────────────────────────────────────────────── */

function readRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr.filter((x): x is string => typeof x === 'string').slice(0, RECENT_LIMIT);
  } catch {
    /* localStorage 解析失敗時降級為空陣列；不影響功能 */
    return [];
  }
}

function writeRecent(id: string) {
  try {
    const prev = readRecent().filter((x) => x !== id);
    const next = [id, ...prev].slice(0, RECENT_LIMIT);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* 寫入失敗（隱私模式 / 配額滿）— 靜默忽略 */
  }
}

/* ════════════════════════════════════════════════════════
 * 主元件
 * ════════════════════════════════════════════════════════ */

export default function CommandPalette() {
  const open = useCommandPaletteStore((s) => s.open);
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  const router = useRouter();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<InputRef | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  /* 一次性載入所有資料（mock + menu pages） */
  const allItems = useMemo(() => getAllItems(), []);

  /* ──────────────────────────────────────────────
   * 關閉時重置狀態（避免下次開啟仍顯示舊查詢）
   * ────────────────────────────────────────────── */
  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  /* 開啟時自動 focus；用 setTimeout 等 antd Modal 完成 mount */
  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  /* ──────────────────────────────────────────────
   * 計算 visible items：
   *   - 空查詢時：先顯示「最近使用」（若有），再顯示命令 + 頁面 top
   *   - 有查詢時：fuzzy score 排序，每組最多 PER_GROUP_LIMIT
   * ────────────────────────────────────────────── */
  const grouped = useMemo(() => {
    const groups = new Map<CommandItemType, CommandItem[]>();
    GROUP_ORDER.forEach((t) => groups.set(t, []));

    if (!query.trim()) {
      /* 空查詢：最近使用置頂（合成「pseudo group」用 command tone 顯示） */
      const recentIds = readRecent();
      const idMap = new Map(allItems.map((i) => [i.id, i] as const));
      const recents = recentIds
        .map((id) => idMap.get(id))
        .filter((x): x is CommandItem => Boolean(x));

      /* 命令永遠展示 */
      groups.set(
        'command',
        allItems.filter((i) => i.type === 'command'),
      );

      /* 頁面取前 PER_GROUP_LIMIT */
      groups.set('page', allItems.filter((i) => i.type === 'page').slice(0, PER_GROUP_LIMIT));
      return { groups, recents };
    }

    /* 有查詢：對所有 item 計分後分組裁切 */
    const scored = allItems
      .map((item) => ({ item, score: scoreItem(item, query.trim()) }))
      .filter((x) => x.score >= 0)
      .sort((a, b) => b.score - a.score);

    for (const { item } of scored) {
      const list = groups.get(item.type);
      if (!list) continue;
      if (list.length < PER_GROUP_LIMIT) list.push(item);
    }

    return { groups, recents: [] as CommandItem[] };
  }, [allItems, query]);

  /* 將 grouped 攤平為 visible 陣列（供鍵盤導航） */
  const visible = useMemo(() => {
    const out: CommandItem[] = [];
    /* 最近使用先進，以維持 UI 順序與導航順序一致 */
    out.push(...grouped.recents);
    for (const t of GROUP_ORDER) {
      const list = grouped.groups.get(t);
      if (list && list.length > 0) out.push(...list);
    }
    return out;
  }, [grouped]);

  /* visible 改變時把 activeIndex 收斂回 0 */
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  /* ↑↓ Enter 鍵盤導航（綁在 Input 上） */
  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    /* IME composing 期間放行 — React event 物件未暴露 isComposing，需從 nativeEvent 取 */
    if (e.nativeEvent.isComposing) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (visible.length === 0 ? 0 : (i + 1) % visible.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (visible.length === 0 ? 0 : (i - 1 + visible.length) % visible.length));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const item = visible[activeIndex];
      if (item) trigger(item);
    }
  };

  /** 觸發 item：紀錄 recent + 跳轉 / 執行 action + 關閉 modal */
  const trigger = (item: CommandItem) => {
    writeRecent(item.id);
    setOpen(false);

    /* 用 setTimeout 讓 modal 收掉動畫後再導航，避免 path 變化打斷退場動效 */
    window.setTimeout(() => {
      if (item.action) {
        item.action();
      } else if (item.to) {
        router.push(item.to);
      }
    }, 0);
  };

  /* 滾動到選中項 */
  useEffect(() => {
    const el = itemRefs.current[activeIndex];
    if (el && listRef.current) {
      el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [activeIndex]);

  /* ──────────────────────────────────────────────
   * Render
   * ────────────────────────────────────────────── */

  /* 將 visible 順序對應的 index 預算好，便於分組渲染時取用 */
  let runningIndex = -1;
  itemRefs.current = [];

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
      closable
      width={640}
      destroyOnHidden
      centered={false}
      mask={{ closable: true }}
      className="zhcb-command-palette"
      style={{
        /* Modal 面板自身的視覺：頂部偏置；圓角/陰影/底色由 className 注入 */
        top: 96,
      }}
      styles={{
        mask: {
          /* 半透明黑 + 高斯模糊（webkit 對 backdrop-filter 支援良好） */
          background: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        },
        body: {
          padding: 0,
        },
      }}
    >
      {/* ── 頂部輸入區 ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: space[3],
          padding: `${space[4]}px ${space[5]}px`,
          borderBottom: `1px solid ${color.border.default}`,
        }}
      >
        <Search size={18} strokeWidth={1.8} color={color.text.tertiary} />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder="搜尋頁面、客戶、案件、證書、命令..."
          variant="borderless"
          style={{
            flex: 1,
            fontSize: text.lg,
            fontFamily: font.body,
            color: color.text.primary,
            padding: 0,
          }}
        />
        <kbd
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 8px',
            border: `1px solid ${color.border.default}`,
            borderRadius: radius.sm,
            background: color.surface.sunken,
            fontFamily: font.mono,
            fontSize: text.xs,
            color: color.text.tertiary,
            lineHeight: 1.4,
          }}
          aria-hidden
        >
          <CommandIcon size={11} strokeWidth={2} /> K
        </kbd>
      </div>

      {/* ── 結果列表 ── */}
      <div
        ref={listRef}
        style={{
          maxHeight: 420,
          overflowY: 'auto',
          padding: `${space[2]}px 0`,
        }}
      >
        {/* 最近使用（僅空查詢時呈現） */}
        {grouped.recents.length > 0 && (
          <Group label="最近使用">
            {grouped.recents.map((item) => {
              runningIndex += 1;
              const idx = runningIndex;
              return (
                <Row
                  key={item.id}
                  ref={(el) => {
                    itemRefs.current[idx] = el;
                  }}
                  item={item}
                  active={idx === activeIndex}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => trigger(item)}
                />
              );
            })}
          </Group>
        )}

        {/* 各分組 */}
        {GROUP_ORDER.map((type) => {
          const list = grouped.groups.get(type) ?? [];
          if (list.length === 0) return null;
          const meta = GROUP_META[type];
          return (
            <Group key={type} label={meta.label}>
              {list.map((item) => {
                runningIndex += 1;
                const idx = runningIndex;
                return (
                  <Row
                    key={item.id}
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    item={item}
                    active={idx === activeIndex}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => trigger(item)}
                  />
                );
              })}
            </Group>
          );
        })}

        {/* 空結果提示 */}
        {visible.length === 0 && (
          <div
            style={{
              padding: `${space[10]}px ${space[6]}px`,
              textAlign: 'center',
              color: color.text.tertiary,
              fontSize: text.sm,
            }}
          >
            找不到「{query}」相關結果
          </div>
        )}
      </div>

      {/* ── 底部快捷鍵提示 ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: space[3],
          padding: `${space[2]}px ${space[5]}px`,
          borderTop: `1px solid ${color.border.default}`,
          background: color.surface.muted,
          fontSize: text.xs,
          color: color.text.tertiary,
          fontFamily: font.body,
        }}
      >
        <div style={{ display: 'flex', gap: space[4], alignItems: 'center' }}>
          <ShortcutHint
            icon={<ArrowUp size={11} />}
            extraIcon={<ArrowDown size={11} />}
            text="導航"
          />
          <ShortcutHint icon={<CornerDownLeft size={11} />} text="選擇" />
          <ShortcutHint label="Esc" text="關閉" />
        </div>
        <div style={{ color: color.text.muted, letterSpacing: tracking.wide }}>
          ZHCB Command Palette
        </div>
      </div>
    </Modal>
  );
}

/* ════════════════════════════════════════════════════════
 * 子元件
 * ════════════════════════════════════════════════════════ */

/** 分組標題 */
function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: space[2] }}>
      <div
        style={{
          padding: `${space[1]}px ${space[5]}px`,
          fontSize: text.xs,
          fontWeight: weight.semibold,
          color: color.text.muted,
          letterSpacing: tracking.widest,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

/** 結果列 — 用 forwardRef 以支援 scrollIntoView */
interface RowProps {
  item: CommandItem;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}

const Row = forwardRef<HTMLDivElement, RowProps>(function Row(
  { item, active, onMouseEnter, onClick },
  ref,
) {
  const meta = GROUP_META[item.type];
  const Icon = meta.Icon;

  /* 圖示 chip 的色票對應 — 與 StatusPill tone 對齊 */
  const chipPalette: Record<typeof meta.tone, { bg: string; fg: string }> = {
    brand: { bg: color.brand.primarySoft, fg: color.brand.primary },
    info: { bg: color.state.infoSoft, fg: color.state.info },
    success: { bg: color.state.successSoft, fg: color.state.success },
    warning: { bg: color.state.warningSoft, fg: color.state.warning },
    danger: { bg: color.state.dangerSoft, fg: color.state.danger },
    neutral: { bg: color.surface.sunken, fg: color.text.secondary },
    muted: { bg: color.surface.sunken, fg: color.text.tertiary },
  };
  const chip = chipPalette[meta.tone];

  return (
    <div
      ref={ref}
      role="option"
      aria-selected={active}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: space[3],
        padding: `${space[2]}px ${space[5]}px`,
        cursor: 'pointer',
        background: active ? color.brand.primarySoft : 'transparent',
        transition: 'background 120ms ease',
      }}
    >
      {/* 左側 3px brand bar — 僅選中時呈現 */}
      {active && (
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 4,
            bottom: 4,
            width: 3,
            background: color.brand.primary,
            borderTopRightRadius: radius.sm,
            borderBottomRightRadius: radius.sm,
          }}
        />
      )}

      {/* icon chip */}
      <span
        style={{
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: radius.md,
          background: chip.bg,
          color: chip.fg,
        }}
      >
        <Icon size={14} strokeWidth={1.8} />
      </span>

      {/* 中間：title + subtitle */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: space[2],
            fontSize: text.md,
            color: color.text.primary,
            fontWeight: weight.medium,
            lineHeight: leading.snug,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</span>
          {item.code && (
            <span
              style={{
                fontFamily: font.mono,
                fontSize: text.sm,
                color: color.text.tertiary,
                fontWeight: weight.regular,
                flexShrink: 0,
              }}
            >
              {item.code}
            </span>
          )}
        </div>
        {item.subtitle && (
          <div
            style={{
              fontSize: text.sm,
              color: color.text.tertiary,
              lineHeight: leading.snug,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: 1,
            }}
          >
            {item.subtitle}
          </div>
        )}
      </div>

      {/* 右側 type tag — StatusPill size=sm muted */}
      <div style={{ flexShrink: 0 }}>
        <StatusPill tone="muted" size="sm">
          {meta.label}
        </StatusPill>
      </div>
    </div>
  );
});

/** 底部單個快捷鍵提示 chip */
function ShortcutHint({
  icon,
  extraIcon,
  label,
  text: textLabel,
}: {
  icon?: React.ReactNode;
  extraIcon?: React.ReactNode;
  label?: string;
  text: string;
}) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          padding: '1px 6px',
          border: `1px solid ${color.border.default}`,
          borderRadius: radius.sm,
          background: color.surface.base,
          fontFamily: font.mono,
          fontSize: text.xs,
          color: color.text.secondary,
          lineHeight: 1.4,
        }}
      >
        {icon}
        {extraIcon}
        {label}
      </span>
      <span>{textLabel}</span>
    </span>
  );
}
